import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { HERO_IMAGE } from "@/data";
import { useI18n } from "@/i18n";
import { MaskedLines, FramedImage } from "./motion";

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen w-full">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-6 pb-16 pt-28 sm:pt-36 md:px-10 lg:grid-cols-12 lg:gap-10 lg:pt-40">
        {/* Left — kinetic type */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="hero-overline mb-8 flex items-center gap-4"
          >
            <span className="h-px w-10 bg-[#9e8d7b]" />
            <h1 className="font-body text-[13px] font-normal uppercase tracking-[0.28em] text-[#3a3a37] md:text-sm">
              {t.hero.overline}
            </h1>
          </motion.div>

          <MaskedLines
            key={t.hero.lines.join("|")}
            lines={t.hero.lines}
            className="hero-title font-display text-[12vw] leading-[0.98] tracking-tight text-[#1c1c1a] sm:text-6xl md:text-[4rem] lg:text-[4.7rem]"
            stagger={0.14}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-9 max-w-xl font-body text-base leading-relaxed text-[#5c5c57] md:text-lg"
          >
            {t.hero.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#projets"
              data-testid="hero-cta-projects"
              className="group inline-flex items-center gap-3 bg-[#1c1c1a] px-8 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-[#f4f4f0] transition-transform duration-300 hover:-translate-y-0.5"
            >
              {t.hero.ctaProjects}
              <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" strokeWidth={1.5} />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="link-underline font-body text-[11px] uppercase tracking-[0.22em] text-[#1c1c1a]"
            >
              {t.hero.ctaContact}
            </a>
          </motion.div>
        </div>

        {/* Right — Rue Étienne-Dumont, Geneva old town */}
        <div className="lg:col-span-5 lg:justify-self-end">
          <motion.div style={{ y: imgY }} className="w-full">
            <FramedImage
              src={HERO_IMAGE}
              alt={t.hero.caption}
              className="aspect-[3/4] w-full"
              imgClass="object-[50%_45%]"
            />
          </motion.div>
          <div className="mt-4 font-body text-[10px] uppercase tracking-[0.2em] text-[#5c5c57]">
            {t.hero.caption}
          </div>
        </div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="pointer-events-none absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
      >
        <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[#5c5c57]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4 text-[#9e8d7b]" strokeWidth={1.5} />
        </motion.span>
      </motion.div>
    </section>
  );
};
